import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { BUSINESS } from "@/lib/siteData";

const DISMISS_KEY = "rp_scam_alert_dismissed";

/**
 * Slim trust banner pinned above the header on every page.
 * Reinforces the anti-scam stance and routes to the scam-awareness post.
 * Dismissal is persisted so returning visitors are not nagged.
 */
export default function ScamAlertBar() {
  // Start hidden to avoid a flash for visitors who already dismissed it.
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* storage unavailable — dismiss for this view only */
    }
    setDismissed(true);
  };

  return (
    <div data-testid="scam-alert-bar" role="region" aria-label="Scam awareness notice" className="bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center justify-center gap-3 text-xs md:text-sm">
        <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
        <p className="text-center leading-snug">
          <strong className="font-semibold">We will never cold-call you.</strong>{" "}
          Suspicious call about your computer? Hang up and ring us:{" "}
          <a href={BUSINESS.phoneHref} className="underline font-semibold whitespace-nowrap">
            {BUSINESS.phone}
          </a>
          <span className="hidden sm:inline">
            {" · "}
            <Link
              to="/blog/recognise-tech-support-scam-call-30-seconds"
              data-testid="scam-alert-learn-more"
              className="underline font-semibold"
            >
              Learn the 30-second scam test
            </Link>
          </span>
        </p>
        <button
          onClick={dismiss}
          data-testid="scam-alert-dismiss"
          aria-label="Dismiss scam awareness notice"
          className="ml-1 p-1 rounded hover:bg-white/10 transition-colors shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
